import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import {
  EVENT_CALENDAR_VIEW,
  type EventCalendarDaySection,
  type EventCalendarItem,
  type EventCalendarRange,
  type EventCalendarView,
} from './event-calendar.types';

export const EVENT_CALENDAR_WEEKDAY_LABELS = [
  'S',
  'M',
  'T',
  'W',
  'T',
  'F',
  'S',
] as const;

export function isEventCalendarView(
  value: string,
): value is EventCalendarView {
  try {
    return (
      value === EVENT_CALENDAR_VIEW.MONTH ||
      value === EVENT_CALENDAR_VIEW.WEEK ||
      value === EVENT_CALENDAR_VIEW.DAY ||
      value === EVENT_CALENDAR_VIEW.AGENDA
    );
  } catch (error) {
    console.error('Failed to check calendar view', { value, error });
    throw error;
  }
}

export function getEventCalendarRange(
  view: EventCalendarView,
  date: Date,
): EventCalendarRange {
  try {
    switch (view) {
      case EVENT_CALENDAR_VIEW.MONTH:
      case EVENT_CALENDAR_VIEW.AGENDA:
        return {
          start: startOfWeek(startOfMonth(date), { weekStartsOn: 0 }),
          end: endOfWeek(endOfMonth(date), { weekStartsOn: 0 }),
        };
      case EVENT_CALENDAR_VIEW.WEEK:
        return {
          start: startOfWeek(date, { weekStartsOn: 0 }),
          end: endOfWeek(date, { weekStartsOn: 0 }),
        };
      case EVENT_CALENDAR_VIEW.DAY:
        return {
          start: startOfDay(date),
          end: endOfDay(date),
        };
      default:
        return {
          start: startOfWeek(date, { weekStartsOn: 0 }),
          end: endOfWeek(date, { weekStartsOn: 0 }),
        };
    }
  } catch (error) {
    console.error('Failed to get event calendar range', { view, date, error });
    throw error;
  }
}

export function shiftEventCalendarDate(
  date: Date,
  view: EventCalendarView,
  direction: 1 | -1,
): Date {
  try {
    switch (view) {
      case EVENT_CALENDAR_VIEW.MONTH:
      case EVENT_CALENDAR_VIEW.AGENDA:
        return addMonths(date, direction);
      case EVENT_CALENDAR_VIEW.WEEK:
        return addWeeks(date, direction);
      case EVENT_CALENDAR_VIEW.DAY:
        return addDays(date, direction);
      default:
        return addWeeks(date, direction);
    }
  } catch (error) {
    console.error('Failed to shift event calendar date', {
      date,
      view,
      direction,
      error,
    });
    throw error;
  }
}

export function formatEventCalendarTitle(
  view: EventCalendarView,
  date: Date,
): string {
  try {
    switch (view) {
      case EVENT_CALENDAR_VIEW.MONTH:
      case EVENT_CALENDAR_VIEW.AGENDA:
        return format(date, 'MMMM yyyy');
      case EVENT_CALENDAR_VIEW.WEEK: {
        const start = startOfWeek(date, { weekStartsOn: 0 });
        const end = endOfWeek(date, { weekStartsOn: 0 });
        const endPattern =
          start.getMonth() === end.getMonth() ? 'd' : 'MMM d';
        return `${format(start, 'MMM d')} – ${format(end, endPattern)}`;
      }
      case EVENT_CALENDAR_VIEW.DAY:
        return format(date, 'EEE, MMM d');
      default:
        return format(date, 'MMMM yyyy');
    }
  } catch (error) {
    console.error('Failed to format event calendar title', {
      view,
      date,
      error,
    });
    throw error;
  }
}

export function formatEventCalendarTimeRange(
  start: Date,
  end: Date,
): string {
  try {
    return `${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`;
  } catch (error) {
    console.error('Failed to format event time range', { start, end, error });
    throw error;
  }
}

export function getEventCalendarDaysInRange(range: EventCalendarRange): Array<Date> {
  try {
    return eachDayOfInterval({ start: range.start, end: range.end });
  } catch (error) {
    console.error('Failed to list calendar days', { range, error });
    throw error;
  }
}

export function buildEventCalendarDaySections(
  days: Array<Date>,
  events: Array<EventCalendarItem>,
): Array<EventCalendarDaySection> {
  try {
    const eventsByDay = new Map<string, Array<EventCalendarItem>>();

    for (const event of events) {
      const key = format(startOfDay(event.start), 'yyyy-MM-dd');
      const existing = eventsByDay.get(key);
      if (existing) {
        existing.push(event);
      } else {
        eventsByDay.set(key, [event]);
      }
    }

    return days.map((day) => {
      const key = format(day, 'yyyy-MM-dd');
      const dayEvents = [...(eventsByDay.get(key) ?? [])].sort(
        (left, right) => left.start.getTime() - right.start.getTime(),
      );
      return { day, events: dayEvents };
    });
  } catch (error) {
    console.error('Failed to group calendar events by day', { error });
    throw error;
  }
}

export function buildEventCalendarAgendaSections(
  events: Array<EventCalendarItem>,
): Array<EventCalendarDaySection> {
  try {
    const dayByKey = new Map<string, Date>();
    for (const event of events) {
      const day = startOfDay(event.start);
      dayByKey.set(format(day, 'yyyy-MM-dd'), day);
    }

    const uniqueDays = Array.from(dayByKey.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([, day]) => day);

    return buildEventCalendarDaySections(uniqueDays, events).filter(
      (section) => section.events.length > 0,
    );
  } catch (error) {
    console.error('Failed to build agenda sections', { error });
    throw error;
  }
}
